/**
 * O AJUSTE da IA aplicado parágrafo a parágrafo (docs/PLANO_EDITOR_AUTOS.md, Bloco 3).
 *
 * A IA devolve a peça inteira em HTML simples (`<p>`, `<strong>`, `<em>`, `<br>`).
 * Trocar o conteúdo do editor por ela apagaria o que a pessoa fez à mão: estilo de
 * parágrafo, recuos, notas de rodapé, marca-texto, tabelas. Aqui os parágrafos da
 * resposta são alinhados com os do editor PELO TEXTO:
 * - parágrafo de texto igual fica intacto (é o mesmo nó);
 * - o que mudou recebe o texto novo e continua com o estilo que tinha (e as notas
 *   de rodapé dele, que a IA não vê, vão para o fim do texto novo);
 * - o novo entra ao lado do vizinho, como Corpo (ou com o estilo do parágrafo que
 *   ele continua); o removido sai.
 * Os alterados ficam destacados até a pessoa limpar o destaque.
 *
 * Com a `base` (o texto que a IA leu, no envio), a junção é a três: o parágrafo
 * que a pessoa mexeu DEPOIS do envio fica como ela deixou, e o que ela apagou
 * não volta — a IA respondeu sobre o texto de antes.
 */
import { Extension, type Editor } from '@tiptap/core';
import { DOMParser as PMDOMParser, Fragment, type Node as PMNode } from '@tiptap/pm/model';
import { Plugin, PluginKey, type Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

import { htmlDaIANosEstilos } from './extensoes/pecaForense';

/* ===================== O texto que vai à IA ===================== */

const escapar = (t: string) => t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Um parágrafo em HTML simples: negrito, itálico e quebra de linha; o resto sai. */
function inlineParaIA(bloco: PMNode): string {
  let html = '';
  bloco.forEach((filho) => {
    if (filho.type.name === 'hardBreak') html += '<br/>';
    else if (filho.isText) {
      let t = escapar(filho.text ?? '');
      const marcas = new Set(filho.marks.map((m) => m.type.name));
      if (marcas.has('italic')) t = `<em>${t}</em>`;
      if (marcas.has('bold')) t = `<strong>${t}</strong>`;
      html += t;
    }
    // Nota de rodapé (e qualquer outro átomo) não vai: a IA não a reproduziria.
  });
  return html;
}

/**
 * O texto vigente do editor como o chat o manda à IA (`<minuta_atual>`): cada
 * parágrafo (inclusive de lista e de tabela) vira um `<p>`, no mesmo vocabulário
 * da resposta. Sem estilo em linha, sai bem mais curto que o HTML do editor, e o
 * backend reconhece pelo texto quando é a minuta que ele mesmo escreveu.
 */
export function htmlParaIA(doc: PMNode): string | null {
  const partes: string[] = [];
  doc.descendants((no) => {
    if (!no.isTextblock) return true;
    const html = inlineParaIA(no);
    if (html.replace(/<br\/>/g, '').trim()) partes.push(`<p>${html}</p>`);
    return false;
  });
  return partes.length ? partes.join('') : null;
}

/* ===================== Alinhamento pelo texto ===================== */

/** O texto que decide "igual": sem notas, com espaços e aspas normalizados (a IA usa aspas curvas). */
export function textoComparavel(bloco: PMNode): string {
  let t = '';
  bloco.forEach((filho) => {
    if (filho.isText) t += filho.text ?? '';
    else if (filho.type.name === 'hardBreak') t += ' ';
  });
  return t
    .replace(/[“”„‟″]/g, '"')
    .replace(/[‘’‚‛′]/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

interface Bloco {
  pos: number;
  no: PMNode;
  texto: string;
}

function blocosDe(doc: PMNode): Bloco[] {
  const lista: Bloco[] = [];
  doc.descendants((no, pos) => {
    if (!no.isTextblock) return true;
    const texto = textoComparavel(no);
    // Parágrafo vazio (respiro entre blocos) não entra no alinhamento: fica onde está.
    if (texto) lista.push({ pos, no, texto });
    return false;
  });
  return lista;
}

/** Trecho a mexer: blocos antigos [ai, af) viram os novos [ni, nf). */
export interface Trecho {
  ai: number;
  af: number;
  ni: number;
  nf: number;
}

/** Teto do quadro de comparação (linhas × colunas) depois de tirar o começo e o fim iguais. */
const TETO_QUADRO = 2_000_000;

/**
 * Os trechos diferentes entre duas listas de textos (subsequência comum mais
 * longa). O começo e o fim iguais saem antes — num ajuste pontual, quase tudo.
 */
export function trechosDiferentes(antigos: string[], novos: string[]): Trecho[] | null {
  let ini = 0;
  while (ini < antigos.length && ini < novos.length && antigos[ini] === novos[ini]) ini++;
  let fimA = antigos.length;
  let fimN = novos.length;
  while (fimA > ini && fimN > ini && antigos[fimA - 1] === novos[fimN - 1]) {
    fimA--;
    fimN--;
  }
  const n = fimA - ini;
  const m = fimN - ini;
  if (n === 0 && m === 0) return [];
  if (n === 0 || m === 0) return [{ ai: ini, af: fimA, ni: ini, nf: fimN }];
  // Grande demais para comparar parágrafo a parágrafo: quem chama recusa o ajuste
  // (parear tudo por posição levava estilo e nota de cada parágrafo ao vizinho).
  if (n * m > TETO_QUADRO) return null;

  // comum[i][j] = tamanho da subsequência comum de antigos[ini+i..] e novos[ini+j..].
  const largura = m + 1;
  const comum = new Uint32Array((n + 1) * largura);
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      comum[i * largura + j] =
        antigos[ini + i] === novos[ini + j]
          ? comum[(i + 1) * largura + j + 1] + 1
          : Math.max(comum[(i + 1) * largura + j], comum[i * largura + j + 1]);
    }
  }
  const trechos: Trecho[] = [];
  let i = 0;
  let j = 0;
  let aberto: Trecho | null = null;
  const fechar = () => {
    if (aberto) trechos.push(aberto);
    aberto = null;
  };
  while (i < n || j < m) {
    if (i < n && j < m && antigos[ini + i] === novos[ini + j]) {
      fechar();
      i++;
      j++;
      continue;
    }
    aberto ??= { ai: ini + i, af: ini + i, ni: ini + j, nf: ini + j };
    if (j < m && (i === n || comum[i * largura + j + 1] >= comum[(i + 1) * largura + j])) {
      j++;
      aberto.nf = ini + j;
    } else {
      i++;
      aberto.af = ini + i;
    }
  }
  fechar();
  return trechos;
}

/* ===================== Aplicação ===================== */

export const CHAVE_DESTAQUE_IA = new PluginKey<DecorationSet>('cm-ef-destaque-ia');

type MetaDestaque = { faixas: [number, number][] } | 'limpar';

/** Os parágrafos que a IA alterou, destacados até a pessoa limpar (ou a IA escrever de novo). */
export const DestaqueIA = Extension.create({
  name: 'destaqueIA',
  addProseMirrorPlugins() {
    return [
      new Plugin<DecorationSet>({
        key: CHAVE_DESTAQUE_IA,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, atual) {
            const meta = tr.getMeta(CHAVE_DESTAQUE_IA) as MetaDestaque | undefined;
            if (meta === 'limpar') return DecorationSet.empty;
            if (meta) {
              return DecorationSet.create(
                tr.doc,
                meta.faixas.map(([de, ate]) => Decoration.node(de, ate, { class: 'cm-ef-ia-alterado' })),
              );
            }
            return atual.map(tr.mapping, tr.doc);
          },
        },
        props: { decorations: (state) => CHAVE_DESTAQUE_IA.getState(state) },
      }),
    ];
  },
});

export const temDestaqueDaIA = (editor: Editor) => (CHAVE_DESTAQUE_IA.getState(editor.state)?.find().length ?? 0) > 0;

/** Os parágrafos da resposta da IA, já nos nós do editor. */
function paragrafosDaIA(editor: Editor, html: string): PMNode[] {
  const dom = new DOMParser().parseFromString(`<body>${htmlDaIANosEstilos(html)}</body>`, 'text/html');
  const doc = PMDOMParser.fromSchema(editor.schema).parse(dom.body);
  const lista: PMNode[] = [];
  doc.descendants((no) => {
    if (!no.isTextblock) return true;
    if (textoComparavel(no)) lista.push(no);
    return false;
  });
  return lista;
}

/** Notas de rodapé de um parágrafo (a IA não as vê; o texto novo as leva no fim). */
function notasDe(bloco: PMNode): PMNode[] {
  const notas: PMNode[] = [];
  bloco.forEach((filho) => {
    if (filho.type.name === 'nota') notas.push(filho);
  });
  return notas;
}

/** Os pares de letras de um texto, com a contagem (para a semelhança). */
function paresDeLetras(t: string): Map<string, number> {
  const s = t.toLowerCase();
  const m = new Map<string, number>();
  for (let i = 0; i < s.length - 1; i++) {
    const p = s.slice(i, i + 2);
    m.set(p, (m.get(p) ?? 0) + 1);
  }
  return m;
}

function dice(a: string, pa: Map<string, number>, b: string, pb: Map<string, number>): number {
  if (a === b) return 1;
  if (a.length < 2 || b.length < 2) return 0;
  let comum = 0;
  const [menor, maior] = pa.size <= pb.size ? [pa, pb] : [pb, pa];
  for (const [p, n] of menor) comum += Math.min(n, maior.get(p) ?? 0);
  return (2 * comum) / (a.length - 1 + b.length - 1);
}

/** Semelhança de dois textos (Dice dos pares de letras): 1 = iguais, 0 = nada em comum. */
export function semelhanca(a: string, b: string): number {
  return dice(a, paresDeLetras(a), b, paresDeLetras(b));
}

/** Abaixo disto, o parágrafo da IA não é "o mesmo, alterado": um sai e o outro entra. */
const LIMIAR_PAR = 0.5;
const TETO_PAREAMENTO = 250_000;
const FAIXA_DIAGONAL = 60;

/**
 * Dentro de um trecho diferente, quais antigos e novos são o mesmo parágrafo
 * alterado: os pares, em ordem, que somam mais semelhança (acima do limiar). Por
 * posição, a nota e o estilo de um parágrafo iam parar no texto do vizinho.
 */
function parear(antigos: string[], novos: string[]): [number, number][] {
  const k = antigos.length;
  const m = novos.length;
  if (!k || !m) return [];
  if (k * m > TETO_PAREAMENTO) {
    // Trecho enorme: só os da mesma posição que se parecem.
    const pares: [number, number][] = [];
    for (let x = 0; x < Math.min(k, m); x++) if (semelhanca(antigos[x], novos[x]) >= LIMIAR_PAR) pares.push([x, x]);
    // (pares de posição: aqui o quadro custaria demais)
    return pares;
  }
  const largura = m + 1;
  const melhor = new Float64Array((k + 1) * largura);
  // Os pares de letras de cada texto, uma vez só; e o tamanho já descarta o par
  // que nem no melhor caso passaria do limiar (Dice ≤ 2·menor/(soma)).
  const pa = antigos.map(paresDeLetras);
  const pn = novos.map(paresDeLetras);
  const sim = (i: number, j: number) => {
    // Só perto da diagonal: parágrafo alterado raramente muda de lugar para longe.
    if (Math.abs(j - (i * m) / k) > FAIXA_DIAGONAL) return 0;
    const la = antigos[i].length;
    const ln = novos[j].length;
    if ((2 * Math.min(la, ln)) / (la + ln) < LIMIAR_PAR) return 0;
    return dice(antigos[i], pa[i], novos[j], pn[j]);
  };
  const cache = new Float64Array(k * m).fill(-1);
  const s = (i: number, j: number) => {
    const c = i * m + j;
    if (cache[c] < 0) cache[c] = sim(i, j);
    return cache[c];
  };
  for (let i = k - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      let v = Math.max(melhor[(i + 1) * largura + j], melhor[i * largura + j + 1]);
      const sij = s(i, j);
      if (sij >= LIMIAR_PAR) v = Math.max(v, sij + melhor[(i + 1) * largura + j + 1]);
      melhor[i * largura + j] = v;
    }
  }
  const pares: [number, number][] = [];
  let i = 0;
  let j = 0;
  while (i < k && j < m) {
    const sij = s(i, j);
    if (sij >= LIMIAR_PAR && melhor[i * largura + j] === sij + melhor[(i + 1) * largura + j + 1]) {
      pares.push([i, j]);
      i++;
      j++;
    } else if (melhor[(i + 1) * largura + j] >= melhor[i * largura + j + 1]) i++;
    else j++;
  }
  return pares;
}

/** O que fazer com o texto: trocar o conteúdo de um antigo, tirá-lo, ou incluir novos depois de um antigo. */
type Operacao =
  | { tipo: 'par'; a: number; n: number }
  | { tipo: 'del'; a: number }
  | { tipo: 'ins'; ns: number[]; depoisDe: number | null };

const ESTILOS_DE_TITULO = new Set(['titulo', 'enderecamento']);
const CELULAS = new Set(['tableCell', 'tableHeader']);

/** Tira um parágrafo, e o contêiner que só tinha ele (item de lista, a lista, a citação); célula fica vazia. */
function remover(tr: Transaction, pos: number) {
  const no = tr.doc.nodeAt(pos);
  if (!no) return;
  const $p = tr.doc.resolve(pos);
  if ($p.depth === 0 || $p.parent.childCount > 1) {
    tr.delete(pos, pos + no.nodeSize);
    return;
  }
  if (CELULAS.has($p.parent.type.name)) {
    tr.replaceWith(pos + 1, pos + no.nodeSize - 1, Fragment.empty);
    return;
  }
  // Sobe enquanto o contêiner só tem este filho (parágrafo → item → lista, citação).
  let d = $p.depth;
  while (d > 1 && $p.node(d - 1).childCount === 1 && !CELULAS.has($p.node(d - 1).type.name)) d--;
  tr.delete($p.before(d), $p.after(d));
}

export interface ResultadoDoAjuste {
  /** Parágrafos que a IA mudou ou incluiu (destacados). */
  alterados: number;
  /** Parágrafos que a IA tirou. */
  removidos: number;
  /** Parágrafos que a pessoa mexeu depois do envio e ficaram como ela deixou. */
  mantidos: number;
  /** O ajuste não foi aplicado: resposta sem texto, ou texto grande demais para comparar. */
  recusa?: 'vazia' | 'grande';
}

/**
 * Aplica a resposta da IA ao texto do editor, parágrafo a parágrafo, numa só
 * transação (um Ctrl+Z desfaz o ajuste inteiro). Não guarda versão nem marca a
 * origem: quem chama faz. Com `recusa`, a transação fica intocada.
 */
export function aplicarAjusteDaIA(editor: Editor, html: string, tr: Transaction, base?: PMNode | null): ResultadoDoAjuste {
  const antigos = blocosDe(tr.doc);
  const novos = paragrafosDaIA(editor, html);
  // Resposta sem texto não é "tirar tudo": é resposta que não veio.
  if (!novos.length) return { alterados: 0, removidos: 0, mantidos: 0, recusa: 'vazia' };
  const textosAntigos = antigos.map((b) => b.texto);
  const textosNovos = novos.map((n) => textoComparavel(n));
  const trechos = trechosDiferentes(textosAntigos, textosNovos);
  if (!trechos) return { alterados: 0, removidos: 0, mantidos: 0, recusa: 'grande' };

  // O que a pessoa fez depois do envio: parágrafos de agora que diferem da base
  // (protegidos) e textos da base que ela tirou (não voltam pela IA).
  const protegidos = new Set<number>();
  const apagadosPelaPessoa = new Set<string>();
  if (base) {
    const textosBase = blocosDe(base).map((b) => b.texto);
    for (const t of trechosDiferentes(textosBase, textosAntigos) ?? []) {
      for (let x = t.ni; x < t.nf; x++) protegidos.add(x);
      for (let x = t.ai; x < t.af; x++) apagadosPelaPessoa.add(textosBase[x]);
    }
  }

  // As operações, na ordem do documento.
  const ops: Operacao[] = [];
  for (const t of trechos) {
    const pares = parear(textosAntigos.slice(t.ai, t.af), textosNovos.slice(t.ni, t.nf));
    let i = t.ai;
    let j = t.ni;
    const fechar = (ateA: number, ateN: number) => {
      for (; i < ateA; i++) ops.push({ tipo: 'del', a: i });
      if (j < ateN) ops.push({ tipo: 'ins', ns: Array.from({ length: ateN - j }, (_, x) => j + x), depoisDe: i > 0 ? i - 1 : null });
      j = ateN;
    };
    for (const [pa, pn] of pares) {
      fechar(t.ai + pa, t.ni + pn);
      ops.push({ tipo: 'par', a: i, n: j });
      i++;
      j++;
    }
    fechar(t.af, t.nf);
  }

  // Do fim para o começo, pelas posições ORIGINAIS: o que se mexe adiante não
  // desloca o que falta. A inclusão depois do bloco `a` vem entre o fim de `a`
  // e o começo do bloco seguinte.
  const chave = (op: Operacao) =>
    op.tipo === 'ins'
      ? op.depoisDe === null
        ? -1
        : antigos[op.depoisDe].pos + antigos[op.depoisDe].no.nodeSize - 0.5
      : antigos[op.a].pos;
  const ordem = [...ops].sort((x, y) => chave(y) - chave(x));

  let mantidos = 0;
  let removidos = 0;
  const marcas: { passo: number; pos: number }[] = [];
  const { schema } = tr.doc.type;
  for (const op of ordem) {
    if (op.tipo === 'del') {
      if (protegidos.has(op.a)) mantidos++;
      else {
        remover(tr, antigos[op.a].pos);
        removidos++;
      }
      continue;
    }
    if (op.tipo === 'par') {
      if (protegidos.has(op.a)) {
        mantidos++;
        continue;
      }
      const pos = antigos[op.a].pos;
      const no = tr.doc.nodeAt(pos);
      if (!no) continue;
      tr.replaceWith(pos + 1, pos + no.nodeSize - 1, novos[op.n].content.append(Fragment.fromArray(notasDe(no))));
      marcas.push({ passo: tr.steps.length, pos });
      continue;
    }
    const ns = op.ns.filter((n) => {
      const volta = apagadosPelaPessoa.has(textosNovos[n]);
      if (volta) mantidos++;
      return !volta;
    });
    if (!ns.length) continue;
    const ancora = op.depoisDe === null ? null : antigos[op.depoisDe];
    // O novo parágrafo continua o estilo do anterior (um pedido depois de um
    // pedido), menos depois de título ou endereçamento: aí entra como Corpo.
    const molde = ancora && !ESTILOS_DE_TITULO.has(String(ancora.no.attrs.estilo ?? '')) ? ancora.no : null;
    const paragrafo = (n: number) => (molde ? molde.type.create(molde.attrs, novos[n].content) : novos[n]);
    const $a = ancora ? tr.doc.resolve(ancora.pos) : null;
    let onde: number;
    let nos: PMNode[];
    let dentro = 0; // quanto o parágrafo fica para dentro do nó inserido (item de lista: 1)
    if (!$a) {
      const primeiro = antigos[0] ? tr.doc.resolve(antigos[0].pos) : null;
      onde = primeiro ? (primeiro.depth === 0 ? antigos[0].pos : primeiro.before(1)) : 0;
      nos = ns.map((n) => novos[n]);
    } else if ($a.parent.type.name === 'listItem' && schema.nodes.listItem) {
      // Dentro de lista: um item novo logo depois do item da âncora, na mesma lista.
      onde = $a.after($a.depth);
      nos = ns.map((n) => schema.nodes.listItem.create(null, paragrafo(n)));
      dentro = 1;
    } else if (CELULAS.has($a.parent.type.name)) {
      // Dentro de célula: na mesma célula, depois do parágrafo da âncora.
      onde = ancora!.pos + ancora!.no.nodeSize;
      nos = ns.map(paragrafo);
    } else {
      onde = $a.depth === 0 ? ancora!.pos + ancora!.no.nodeSize : $a.after(1);
      nos = ns.map((n) => ($a.depth === 0 ? paragrafo(n) : novos[n]));
    }
    tr.insert(onde, nos);
    let p = onde;
    for (const no of nos) {
      marcas.push({ passo: tr.steps.length, pos: p + dentro });
      p += no.nodeSize;
    }
  }

  const faixas: [number, number][] = [];
  for (const { passo, pos } of marcas) {
    const final = tr.mapping.slice(passo).map(pos);
    const no = tr.doc.nodeAt(final);
    if (no?.isTextblock) faixas.push([final, final + no.nodeSize]);
  }
  tr.setMeta(CHAVE_DESTAQUE_IA, { faixas } satisfies MetaDestaque);
  return { alterados: faixas.length, removidos, mantidos };
}

export const limparDestaqueDaIA = (editor: Editor) => {
  editor.view.dispatch(editor.state.tr.setMeta(CHAVE_DESTAQUE_IA, 'limpar' satisfies MetaDestaque));
};
